import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Icon } from "@/lib/icons";
import { cn } from "@/lib/utils";
import { useApp } from "@/hooks/use-app-context";
import type { ComplaintTab } from "./complaints.constants";

interface ComplaintsSidebarProps {
  activeTab: ComplaintTab;
  onTabChange: (tab: ComplaintTab) => void;
  isAuthenticated: boolean;
}

const NAV_ITEMS = [
  { value: "submit", icon: "send" as const, labelKey: "complaintsNew" },
  { value: "history", icon: "messageSquare" as const, labelKey: "complaintsHistory" },
] as const;

export function ComplaintsSidebar({ activeTab, onTabChange, isAuthenticated }: ComplaintsSidebarProps) {
  const { t } = useApp();

  const visibleItems = NAV_ITEMS.filter(
    (item) => item.value === "submit" || isAuthenticated
  );

  return (
    <>
      <nav className="hidden lg:block">
        <div className="space-y-1">
          {visibleItems.map((item) => {
            const active = activeTab === item.value;
            return (
              <button
                key={item.value}
                type="button"
                onClick={() => onTabChange(item.value)}
                className={cn(
                  "group relative flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition-colors overflow-hidden",
                  active
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <span className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_0%,hsl(var(--primary)/0.1),transparent_60%)] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                <span
                  className={cn(
                    "absolute left-0 top-1/2 h-5 w-px -translate-y-1/2 rounded-full transition-all",
                    active ? "bg-primary opacity-100" : "bg-transparent opacity-0"
                  )}
                />
                <span
                  className={cn(
                    "relative grid h-7 w-7 shrink-0 place-items-center rounded-lg transition-colors",
                    active
                      ? "bg-primary/[0.08] text-primary"
                      : "text-muted-foreground group-hover:text-foreground"
                  )}
                >
                  <Icon name={item.icon} className="h-4 w-4" />
                </span>
                <span className="relative min-w-0 flex-1 truncate">
                  {t[item.labelKey as keyof typeof t] || (item.value === 'submit' ? 'Nouveau signalement' : 'Mes signalements')}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      <div className="lg:hidden">
        <Sheet>
          <SheetTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 rounded-xl border border-border/40 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              <Icon name="menu" className="h-4 w-4" />
              {t[NAV_ITEMS.find((n) => n.value === activeTab)?.labelKey as keyof typeof t] || (activeTab === 'submit' ? 'Nouveau signalement' : 'Mes signalements')}
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-4">
            <div className="space-y-1 pt-4">
              {visibleItems.map((item) => {
                const active = activeTab === item.value;
                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => { onTabChange(item.value); }}
                    className={cn(
                      "group relative flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition-colors overflow-hidden",
                      active
                        ? "text-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <span className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_0%,hsl(var(--primary)/0.1),transparent_60%)] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    <Icon
                      name={item.icon}
                      className={cn(
                        "relative h-4 w-4 shrink-0",
                        active ? "text-primary" : "text-muted-foreground"
                      )}
                    />
                    <span className="relative min-w-0 flex-1 truncate">
                      {t[item.labelKey as keyof typeof t] || (item.value === 'submit' ? 'Nouveau signalement' : 'Mes signalements')}
                    </span>
                  </button>
                );
              })}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
}
